import React, { useState, useMemo, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Server, 
  Laptop, 
  ShieldAlert, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  Download, 
  ChevronRight, 
  RefreshCcw, 
  SlidersHorizontal, 
  FileText,
  Activity,
  Loader2
} from 'lucide-react';
import { ToolCall, FilterNode, FilterStatus } from '../types';
import { formatTimeAgo } from '../services/timeAgo';

interface ActivityFeedProps {
  calls: ToolCall[];
  onSelectCall: (call: ToolCall) => void;
  selectedCallId?: string;
  activeNodeFilter: FilterNode;
  onChangeNodeFilter: (node: FilterNode) => void;
  activeStatusFilter: FilterStatus;
  onChangeStatusFilter: (status: FilterStatus) => void;
  isSimulated?: boolean;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  calls,
  onSelectCall,
  selectedCallId,
  activeNodeFilter,
  onChangeNodeFilter,
  activeStatusFilter,
  onChangeStatusFilter,
  isSimulated = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState<number>(15);
  const [now, setNow] = useState<number>(Date.now());

  // Keep relative "time ago" updated in real-time
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Filter and search logic
  const filteredCalls = useMemo(() => {
    return calls.filter((call) => {
      // Node filter
      if (activeNodeFilter !== 'All' && call.targetNode !== activeNodeFilter) {
        return false;
      }

      // Status filter
      if (activeStatusFilter !== 'All') {
        if (activeStatusFilter === '200' && call.status !== 200) return false;
        if (activeStatusFilter === '403' && call.status !== 403) return false;
        if (activeStatusFilter === '409' && call.status !== 409) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = call.toolName.toLowerCase().includes(query);
        const matchesAction = call.action.toLowerCase().includes(query);
        const matchesEndpoint = call.endpoint.toLowerCase().includes(query);
        const matchesIp = call.clientIp.toLowerCase().includes(query);
        const matchesHost = call.nodeDetails.host.toLowerCase().includes(query);
        const matchesId = call.id.toLowerCase().includes(query);
        const matchesParams = JSON.stringify(call.parameters).toLowerCase().includes(query);
        const matchesError = (call.errorMessage || '').toLowerCase().includes(query);

        return (
          matchesName ||
          matchesAction ||
          matchesEndpoint ||
          matchesIp ||
          matchesHost ||
          matchesId ||
          matchesParams ||
          matchesError
        );
      }

      return true;
    });
  }, [calls, activeNodeFilter, activeStatusFilter, searchQuery]);

  // Paginated list
  const visibleCalls = useMemo(() => {
    return filteredCalls.slice(0, pageSize);
  }, [filteredCalls, pageSize]);

  const handleExportJson = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(filteredCalls, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `telemetry-events-${new Date().toISOString().substring(0, 19)}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  return (
    <div className="rounded-xl bg-slate-900/70 border border-slate-800 overflow-hidden shadow-xl">
      
      {/* Header & Controls Toolbar */}
      <div className="p-4 sm:p-5 border-b border-slate-800/80 bg-slate-900/90 space-y-4">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
              <span>Live Tool Call Telemetry</span>
              <span className="px-2 py-0.5 rounded-full bg-cyan-950/70 text-cyan-300 border border-cyan-800/70 text-xs font-mono font-normal">
                {filteredCalls.length} events
              </span>
            </h2>
            <p className="text-xs text-slate-400 font-mono mt-0.5">
              Chronological execution trace across Mac agent & VPS gateway nodes
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExportJson}
              title="Export filtered events as JSON"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 text-xs font-mono transition-colors"
            >
              <Download className="w-3.5 h-3.5 text-cyan-400" />
              <span>Export JSON</span>
            </button>
          </div>
        </div>

        {/* Filters & Search Row */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 pt-1">
          
          {/* Search Box */}
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tool, action, endpoint, client IP, or params..."
              className="w-full bg-slate-950/80 border border-slate-700/80 rounded-lg pl-9 pr-8 py-1.5 text-xs text-slate-200 placeholder-slate-500 font-mono focus:outline-none focus:border-cyan-500 transition-colors"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 text-xs"
              >
                ✕
              </button>
            )}
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
            
            {/* Target Node Filter */}
            <div className="flex items-center bg-slate-950 p-1 rounded-lg border border-slate-800">
              <span className="text-slate-400 px-2 text-[11px]">Node:</span>
              {(['All', 'Mac', 'VPS'] as FilterNode[]).map((node) => (
                <button
                  key={node}
                  onClick={() => onChangeNodeFilter(node)}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                    activeNodeFilter === node
                      ? node === 'Mac'
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                        : node === 'VPS'
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                        : 'bg-slate-700/80 text-white border border-slate-600'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {node === 'Mac' ? 'Mac Silicon' : node === 'VPS' ? 'VPS Linux' : 'All'}
                </button>
              ))}
            </div>

            {/* Status Filter */}
            <div className="flex items-center bg-slate-950 p-1 rounded-lg border border-slate-800">
              <span className="text-slate-400 px-2 text-[11px]">Status:</span>
              {[
                { label: 'All', val: 'All' },
                { label: '200 OK', val: '200' },
                { label: '403 Blocked', val: '403' },
                { label: '409 Conflict', val: '409' },
              ].map((st) => (
                <button
                  key={st.val}
                  onClick={() => onChangeStatusFilter(st.val as FilterStatus)}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                    activeStatusFilter === st.val
                      ? st.val === '200'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                        : st.val === '403'
                        ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                        : st.val === '409'
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                        : 'bg-slate-700/80 text-white border border-slate-600'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {st.label}
                </button>
              ))}
            </div>

          </div>

        </div>

      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse text-xs font-mono">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 text-[11px] uppercase tracking-wider">
              <th className="py-3 px-4 font-semibold">Timestamp</th>
              <th className="py-3 px-4 font-semibold">Status</th>
              <th className="py-3 px-4 font-semibold">Target Node</th>
              <th className="py-3 px-4 font-semibold">Tool & Action</th>
              <th className="py-3 px-4 font-semibold">Endpoint</th>
              <th className="py-3 px-4 font-semibold text-right">Duration</th>
              <th className="py-3 px-4 font-semibold">Client IP</th>
              <th className="py-3 px-4 font-semibold text-center">Inspect</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {visibleCalls.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-14 text-center text-slate-400 font-sans">
                  {calls.length === 0 && !isSimulated ? (
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </div>
                      <p className="text-sm font-semibold text-slate-200">Synchronizing Live Telemetry Stream</p>
                      <p className="text-xs text-slate-400 font-mono">Fetching latest audit events from 9router.hanavy.online...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <FileText className="w-8 h-8 text-slate-600" />
                      <p className="text-sm font-medium text-slate-400">No telemetry events match your criteria</p>
                      <p className="text-xs text-slate-400">Try adjusting your node, status, or search filters.</p>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              visibleCalls.map((call) => {
                const isMac = call.targetNode === 'Mac';
                const is200 = call.status === 200;
                const is403 = call.status === 403;
                const is409 = call.status === 409;
                const isSelected = selectedCallId === call.id;

                const timeStr = new Date(call.timestamp).toLocaleTimeString([], {
                  hour12: false,
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                });

                return (
                  <tr
                    key={call.id}
                    onClick={() => onSelectCall(call)}
                    className={`group cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-slate-800/80 border-l-2 border-l-cyan-400'
                        : 'hover:bg-slate-800/40 hover:border-l-2 hover:border-l-slate-600'
                    }`}
                  >
                    {/* Timestamp */}
                    <td className="py-3 px-4 whitespace-nowrap text-[11px]">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5 text-slate-300 font-medium">
                          <Clock className="w-3 h-3 text-cyan-400/80" />
                          <span>{timeStr}</span>
                        </div>
                        <span className="text-[10px] text-cyan-400/90 font-sans font-medium pl-4.5 tracking-tight">
                          {formatTimeAgo(call.timestamp, now)}
                        </span>
                      </div>
                    </td>

                    {/* Status Badge */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold ${
                        is200 ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' :
                        is403 ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40' :
                        'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                      }`}>
                        {is200 && <CheckCircle2 className="w-3 h-3" />}
                        {is403 && <ShieldAlert className="w-3 h-3" />}
                        {is409 && <AlertTriangle className="w-3 h-3" />}
                        <span>{call.status}</span>
                      </span>
                    </td>

                    {/* Target Node Badge */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium ${
                        isMac
                          ? 'bg-purple-950/70 text-purple-300 border border-purple-800/60'
                          : 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60'
                      }`}>
                        {isMac ? <Laptop className="w-3 h-3 text-purple-400" /> : <Server className="w-3 h-3 text-cyan-400" />}
                        <span>{isMac ? 'Mac (Apple Silicon)' : 'VPS (Linux)'}</span>
                      </span>
                    </td>

                    {/* Tool Name & Action */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div>
                        <span className="font-bold text-slate-200 group-hover:text-cyan-300 transition-colors">
                          {call.toolName}
                        </span>
                        <span className="text-slate-400 text-[10px] block">
                          {call.action}
                        </span>
                      </div>
                    </td>

                    {/* Endpoint */}
                    <td className="py-3 px-4 whitespace-nowrap text-slate-400 text-[11px]">
                      <code className="text-slate-300">{call.endpoint}</code>
                    </td>

                    {/* Duration */}
                    <td className="py-3 px-4 whitespace-nowrap text-right text-slate-300 font-semibold">
                      <span className={call.durationMs > 150 ? 'text-amber-300' : 'text-slate-300'}>
                        {call.durationMs}ms
                      </span>
                    </td>

                    {/* Client IP */}
                    <td className="py-3 px-4 whitespace-nowrap text-slate-400 text-[11px]">
                      {call.clientIp}
                    </td>

                    {/* Action Inspector Button */}
                    <td className="py-3 px-4 whitespace-nowrap text-center">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectCall(call);
                        }}
                        className="p-1 rounded bg-slate-800 hover:bg-cyan-500/20 text-slate-400 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/40 transition-colors"
                        title="View tool call payload & details"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination & Footer summary */}
      <div className="p-3.5 border-t border-slate-800/80 bg-slate-950/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs font-mono text-slate-400">
        <div>
          Showing <span className="text-slate-200 font-semibold">{visibleCalls.length}</span> of{' '}
          <span className="text-slate-200 font-semibold">{filteredCalls.length}</span> filtered events (
          {calls.length} total in buffer)
        </div>

        {filteredCalls.length > visibleCalls.length && (
          <button
            onClick={() => setPageSize((prev) => prev + 15)}
            className="px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-mono transition-colors"
          >
            Load 15 More Events
          </button>
        )}
      </div>

    </div>
  );
};
